// Styles for mos 3.0

import '@emotion/react';
import { ThemeType } from './theme';
import expressionToWidth from './utils/helpers';
import pxToEm from './utils/pxToEm';
import pxToRem from './utils/pxToRem';

const makeTheme = <T extends ThemeType>(t: T) => t;

type SpaceTokensMap = {
  [key: string]: string;
};

function generateSpaces(
  start = 0,
  tokensMap: SpaceTokensMap = { auto: 'auto', 'sp-0': '0', '-sp-0': '0' }
): SpaceTokensMap {
  if (start === 80) return tokensMap;
  let next;
  if (start < 4) {
    next = start + 2;
  } else if (start < 24) {
    next = start + 4;
  } else {
    next = start + 8;
  }
  let key: keyof SpaceTokensMap = `sp-${next}`;
  let negativeKey: keyof SpaceTokensMap = `-sp-${next}`;
  let value = pxToRem(next);
  let negativeValue = pxToRem(0 - next);
  tokensMap[key] = value;
  tokensMap[negativeKey] = negativeValue;
  return generateSpaces(next, tokensMap);
}

const theme30 = makeTheme({
  space: Object.assign(generateSpaces(), {
    accordionIconMarginTop: pxToRem(1),
    accordionPaddingY: pxToRem(24),

    btn32PaddingTop: pxToRem(7),
    btn32PaddingX: pxToRem(11),
    btn32PaddingBottom: pxToRem(7),

    btn40PaddingTop: pxToRem(11),
    btn40PaddingX: pxToRem(15),
    btn40PaddingBottom: pxToRem(11),

    btn48PaddingTop: pxToRem(13),
    btn48PaddingX: pxToRem(19),
    btn48PaddingBottom: pxToRem(13),

    btn56PaddingTop: pxToRem(15),
    btn56PaddingX: pxToRem(23),
    btn56PaddingBottom: pxToRem(15),

    btnBorderlessPaddingX: '0',

    contentPaddingX: pxToRem(16),

    tabSize1PaddingTop: pxToRem(3),
    tabSize1PaddingX: pxToRem(11),
    tabSize1PaddingBottom: pxToRem(3),

    tabSize2PaddingTop: pxToRem(3),
    tabSize2PaddingX: pxToRem(11),
    tabSize2PaddingBottom: pxToRem(3),

    tabSize3PaddingTop: pxToRem(3),
    tabSize3PaddingX: pxToRem(11),
    tabSize3PaddingBottom: pxToRem(3),

    tabSize4PaddingTop: pxToRem(3),
    tabSize4PaddingX: pxToRem(11),
    tabSize4PaddingBottom: pxToRem(3),

    checkboxTickLeft: '0.28rem',
    checkboxTickTop: '0.3rem',

    fieldPaddingX: pxToRem(16),
    fieldPaddingY: pxToRem(8),

    errorIconMarginRight: pxToRem(6),
    errorIconMarginTop: pxToRem(-3),

    labelledFieldMarginTop: pxToRem(8),

    calendarDayNamesPb: pxToRem(12),

    datepickerSpace: pxToRem(16),
    '-datepickerSpace': pxToRem(-16),
    datepickerHintMarginBottom: pxToRem(12),

    /* Modal */
    /* modal header */
    modalHeaderPaddingTopSmall: pxToRem(24),
    modalHeaderPaddingTop: pxToRem(28),

    modalHeaderPaddingRightSmall: pxToRem(16),
    modalHeaderPaddingRight: pxToRem(40),

    modalHeaderPaddingBottomSmall: pxToRem(4),
    modalHeaderPaddingBottom: pxToRem(8),

    modalHeaderPaddingLeftSmall: pxToRem(16),
    modalHeaderPaddingLeft: pxToRem(40),

    /* modal heading / title */
    modalHeadingMarginBottom: pxToRem(12),
    modalHeadingPaddingRightSmall: pxToRem(44),
    modalHeadingPaddingRight: pxToRem(56),

    /* modal body */
    modalBodyPaddingTopSmall: pxToRem(20),
    modalBodyPaddingTop: pxToRem(24),

    modalBodyPaddingRightSmall: pxToRem(16),
    modalBodyPaddingRight: pxToRem(40),

    modalBodyPaddingBottomSmall: pxToRem(20),
    modalBodyPaddingBottom: pxToRem(40),

    modalBodyPaddingLeftSmall: pxToRem(16),
    modalBodyPaddingLeft: pxToRem(40),

    /* modal footer */
    modalFooterPaddingTopSmall: pxToRem(16),
    modalFooterPaddingTop: pxToRem(20),

    modalFooterPaddingRightSmall: pxToRem(16),
    modalFooterPaddingRight: pxToRem(40),

    modalFooterPaddingBottomSmall: pxToRem(20),
    modalFooterPaddingBottom: pxToRem(20),

    modalFooterPaddingLeftSmall: pxToRem(16),
    modalFooterPaddingLeft: pxToRem(40),

    /* modal close */
    modalCloseBtnMT: pxToRem(20),
    modalBigCloseBtnMT: pxToRem(24),
    modalCloseBtnMR: pxToRem(16),
    modalBigCloseBtnMR: pxToRem(24),

    /* InfoModal */
    /* modal header */
    infoModalHeaderPaddingTopSmall: pxToRem(16),
    infoModalHeaderPaddingTop: pxToRem(32),

    infoModalHeaderPaddingRightSmall: pxToRem(16),
    infoModalHeaderPaddingRight: pxToRem(40),

    infoModalHeaderPaddingBottomSmall: 0,
    infoModalHeaderPaddingBottom: 0,

    infoModalHeaderPaddingLeftSmall: pxToRem(16),
    infoModalHeaderPaddingLeft: pxToRem(40),

    /* Message */
    messagePaddingTopSmall: pxToRem(16),
    messagePaddingTop: pxToRem(20),

    messagePaddingRightSmall: pxToRem(16),
    messagePaddingRight: pxToRem(32),

    messagePaddingBottomSmall: pxToRem(16),
    messagePaddingBottom: pxToRem(20),

    messagePaddingLeftSmall: pxToRem(16),
    messagePaddingLeft: pxToRem(20),

    messageIconMarginBottom: pxToRem(16),
    messageIconMarginRight: pxToRem(16),

    tooltipPaddingX: pxToRem(10),
    tooltipPaddingY: pxToRem(12),

    comboboxListPadding: pxToRem(4),

    /* FileUploader */
    fileCardPaddingTop: pxToRem(11),
    fileCardPaddingRight: pxToRem(12),
    fileCardPaddingBottom: pxToRem(11),
    fileCardPaddingLeft: pxToRem(20),
    fileCancelBtnIconOffset: pxToRem(6),
    fileRemoveBtnIconOffset: pxToRem(6),
    fileUploaderMessageBtnTopOffset: pxToRem(8),
    fileUploaderMessageBtnTopOffsetBig: pxToRem(8),
    fileUploaderMessageBtnRightOffset: pxToRem(8),
    fileUploaderMessageBtnRightOffsetBig: pxToRem(8),
    fileUploaderMessagePaddingRight: pxToRem(32),

    fileUploaderIconTopOffset: pxToRem(2),

    /* Stepper */
    stepperFieldPadding: pxToRem(3),
    stepperButtonPadding: pxToRem(7),
    stepperFieldInputPaddingY: pxToRem(7),
    stepperFieldInputPaddingX: pxToRem(15),
    /* Headings */
    h1MarginTop: '1.428em',
    h1MarginBottom: '0.571em',
    h1BigMarginTop: '1.25em',
    h1BigMarginBottom: '0.5em',

    h2MarginTop: '1.666em',
    h2MarginBottom: '0.666em',
    h2BigMarginTop: '1.538em',
    h2BigMarginBottom: '0.615em',

    h3MarginTop: '1.6em',
    h3MarginBottom: '0.8em',
    h3BigMarginTop: '1.818em',
    h3BigMarginBottom: '0.727em',

    h4MarginTop: '1.777em',
    h4MarginBottom: '0.666em',
    h4BigMarginTop: '1.777em',
    h4BigMarginBottom: '0.666em',

    h5MarginTop: '1.5em',
    h5MarginBottom: '0.75em',
    h5BigMarginTop: '1.5em',
    h5BigMarginBottom: '0.75em',

    h6MarginTop: '1.714em',
    h6MarginBottom: '0.857em',
    h6BigMarginTop: '1.714em',
    h6BigMarginBottom: '0.857em',

    /* Navigation */
    navLinkSize1PaddingTop: pxToRem(4),
    navLinkSize1PaddingX: pxToRem(12),
    navLinkSize1PaddingBottom: pxToRem(2),

    navLinkSize2PaddingTop: pxToRem(4),
    navLinkSize2PaddingX: pxToRem(12),
    navLinkSize2PaddingBottom: pxToRem(2),
  }) as Record<string | number, string>,
  colors: {
    brand: '#cc2222',
    body: '#E6E9ED',
    focus: '#2684FF',
    transparent: 'rgba(255, 255, 255, 0)',

    white: '#fff',

    /** @deprecated */
    'gray-05': '#F3F5F7',
    'gray-10': '#F5F6F7',
    'gray-15': '#E6E9ED',
    'gray-20': '#D1D8E0',
    'gray-30': '#BCC4CF',
    'gray-35': '#A8B1BD',
    'gray-40': '#959EAB',
    'gray-50': '#828C99',
    'gray-60': '#717985',
    'gray-65': '#5D6570',
    'gray-70': '#4B525C',
    'gray-80': '#383D45',
    'gray-85': '#24282E',
    'gray-90': '#13151A',
    black: '#000',

    /** @deprecated */
    'blue-05': '#EBF1FF',
    'blue-10': '#F0F7FF',
    'blue-15': '#DEEDFF',
    'blue-20': '#C4DFFF',
    'blue-30': '#99C7FF',
    'blue-35': '#73B0FF',
    'blue-40': '#4D9AFF',
    'blue-50': '#2684FF',
    'blue-60': '#0C6BF2',
    'blue-65': '#0459E0',
    'blue-70': '#0043BF',
    'blue-80': '#003099',
    'blue-85': '#002173',
    'blue-90': '#00134C',

    /** @deprecated */
    'success-05': '#E9FCF3',
    'success-10': '#EBFAF5',
    'success-15': '#D2F7EB',
    'success-20': '#AAF2DA',
    'success-30': '#7EE6C3',
    'success-35': '#57D9AD',
    'success-40': '#3DCC9C',
    'success-50': '#26BF8C',
    'success-60': '#00A674',
    'success-65': '#008C62',
    'success-70': '#007352',
    'success-80': '#005940',
    'success-85': '#00402F',
    'success-90': '#00261C',

    /** @deprecated */
    'warning-05': '#FFF7E6',
    'warning-10': '#FFF5E8',
    'warning-15': '#FFEACC',
    'warning-20': '#FFDEB0',
    'warning-30': '#FFCE91',
    'warning-35': '#FFBC6B',
    'warning-40': '#FEA84C',
    'warning-50': '#FE9026',
    'warning-60': '#F27F0C',
    'warning-65': '#D46A00',
    'warning-70': '#AB5300',
    'warning-80': '#874100',
    'warning-85': '#663000',
    'warning-90': '#401D00',

    /** @deprecated */
    'error-05': '#FFF2F2',
    'error-10': '#FFF2F5',
    'error-15': '#FEE5EA',
    'error-20': '#FFD4DC',
    'error-30': '#FFB3C2',
    'error-35': '#FF8CA3',
    'error-40': '#FF597A',
    'error-50': '#F23057',
    'error-60': '#E61740',
    'error-65': '#CC0029',
    'error-70': '#A10020',
    'error-80': '#80001A',
    'error-85': '#660016',
    'error-90': '#4D0010',

    'biege-05': '#F7F5EE',
    'biege-25': '#E1D0B9',
    'biege-60': '#C5AD8D',
    'biege-80': '#5B422C',

    'green-05': '#F2FDA5',
    'green-25': '#CFDE64',
    'green-60': '#B7CC1A',

    'red-05': '#FFD8D8',
    'red-25': '#FFABAB',
    'blue-25': '#8AC0FF',

    /* purple */
    'purple-10': '#FAF2FF',
    'purple-15': '#F3E2FE',
    'purple-20': '#E6D1FF',
    'purple-30': '#D7B5FF',
    'purple-35': '#C898FA',
    'purple-40': '#B77CF2',
    'purple-50': '#A35FE3',
    'purple-60': '#8B41CC',
    'purple-65': '#7625B8',
    'purple-70': '#62169C',
    'purple-80': '#520787',
    'purple-85': '#3E0266',
    'purple-90': '#2F004D',

    /* Accordion */
    accordionColorText: '#13151A',
    accordionColorHoverText: '#383D45',
    accordionColorActiveText: '#4B525C',
    accordionColorBorder: '#E6E9ED',

    /* Button Primary */
    primaryBtnBg: '#003099',
    primaryBtnBorder: '#003099',
    primaryBtnText: 'white',

    primaryBtnHoverBg: '#0043BF',
    primaryBtnHoverBorder: '#0043BF',
    primaryBtnHoverText: 'white',

    primaryBtnActiveBg: '#0459E0',
    primaryBtnActiveBorder: '#0459E0',
    primaryBtnActiveText: 'white',

    primaryBtnDisabledBg: '#E6E9ED',
    primaryBtnDisabledBorder: '#E6E9ED',
    primaryBtnDisabledBText: '#959EAB',

    /* Button Secondary */
    secondaryBtnBg: '#DEEDFF',
    secondaryBtnBorder: '#DEEDFF',
    secondaryBtnText: '#003099',

    secondaryBtnHoverBg: '#C4DFFF',
    secondaryBtnHoverBorder: '#C4DFFF',
    secondaryBtnHoverText: '#003099',

    secondaryBtnActiveBg: '#99C7FF',
    secondaryBtnActiveBorder: '#99C7FF',
    secondaryBtnActiveText: '#003099',

    secondaryBtnDisabledBg: '#EEEFF2',
    secondaryBtnDisabledBorder: '#EEEFF2',
    secondaryBtnDisabledBText: '#959EAB',

    /* Button Tertiary */
    tertiaryBtnBg: 'white',
    tertiaryBtnBorder: '#BCC4CF',
    tertiaryBtnText: '#13151A',

    tertiaryBtnHoverBg: '#F5F6F7',
    tertiaryBtnHoverBorder: '#BCC4CF',
    tertiaryBtnHoverText: '#13151A',

    tertiaryBtnActiveBg: '#E6E9ED',
    tertiaryBtnActiveBorder: '#BCC4CF',
    tertiaryBtnActiveText: '#13151A',

    tertiaryBtnDisabledBg: '#EEEFF2',
    tertiaryBtnDisabledBorder: '#BCC4CF',
    tertiaryBtnDisabledBText: '#959EAB',

    /* Button Borderless */
    borderlessBtnBg: 'rgba(255, 255, 255, 0)',
    borderlessBtnBorder: 'rgba(255, 255, 255, 0)',
    borderlessBtnText: '#003099',

    borderlessBtnHoverBg: 'rgba(255, 255, 255, 0)',
    borderlessBtnHoverBorder: 'rgba(255, 255, 255, 0)',
    borderlessBtnHoverText: '#0043BF',

    borderlessBtnActiveBg: 'rgba(255, 255, 255, 0)',
    borderlessBtnActiveBorder: 'rgba(255, 255, 255, 0)',
    borderlessBtnActiveText: '#0459E0',

    borderlessBtnDisabledBg: 'rgba(255, 255, 255, 0)',
    borderlessBtnDisabledBorder: 'rgba(255, 255, 255, 0)',
    borderlessBtnDisabledBText: '#959EAB',

    /* Field */
    fieldBorderColor: '#828C99',
    fieldBgColor: 'white',
    fieldTextColor: '#13151A',
    fieldPlaceholderColor: '#5D6570',

    fieldHoverBorderColor: '#717985',
    fieldHoverBgColor: '#F5F6F7',

    fieldFocusBorderColor: '#003099',

    fieldErrorBorderColor: '#CC0029',
    fieldErrorBgColor: 'white',

    fieldErrorHoverBorderColor: '#A10020',

    fieldDisabledBorderColor: '#BCC4CF',
    fieldDisabledBgColor: '#E6E9ED',
    fieldDisabledTextColor: '#959EAB',

    labelColor: '#13151A',
    errorColor: '#CC0029',
    hintColor: '#5D6570',

    /* Checkables */
    checkableBgColor: 'white',
    checkableBorderColor: '#828C99',
    checkableLabelColor: '#13151A',
    checkableHoverBgColor: '#F5F6F7',
    checkableHoverBorderColor: '#717985',
    checkableActiveBgColor: '#E6E9ED',
    checkableActiveBorderColor: '#5D6570',

    checkableTickColor: 'white',

    checkableCheckedColor: '#003099',
    checkableCheckedHoverColor: '#0043BF',
    checkableCheckedActiveColor: '#0459E0',

    checkableErrorColor: '#CC0029',
    checkableErrorHoverColor: '#A10020',
    checkableErrorActiveColor: '#80001A',

    checkableDisabledBgColor: '#E6E9ED',
    checkableDisabledCheckedBgColor: '#BCC4CF',
    checkableDisabledBorderColor: '#BCC4CF',
    checkableDisabledTickColor: 'white',
    checkableDisabledText: '#959EAB',
    checkableDisabledLabelColor: '#959EAB',

    /* Calendar */
    calendarDayOfWeek: '#5D6570',

    calendarBtnBorderColor: '#D1D8E0',

    calendarBtnBg: 'white',
    calendarBtnHoverBg: '#DEEDFF',
    calendarBtnActiveBg: '#C4DFFF',
    calendarBtnText: '#13151A',

    calendarBtnSelectedBg: '#003099',
    calendarBtnSelectedHoverBg: '#0043BF',
    calendarBtnSelectedActiveBg: '#0459E0',
    calendarSelectedText: 'white',

    calendarIntervalBg: '#99C7FF',
    calendarIntervalHoverBg: '#73B0FF',
    calendarIntervalActiveBg: '#4D9AFF',

    calendarBtnDisabledBg: '#F5F6F7',
    calendarBtnDisabledText: '#959EAB',

    /* Datepicker */
    datepickerCalendarBtnBg: '#DEEDFF',
    datepickerCalendarBtnColor: '#003099',

    datepickerCalendarBtnHoverBg: '#C4DFFF',
    datepickerCalendarBtnHoverColor: '#003099',

    datepickerCalendarBtnActiveBg: '#99C7FF',
    datepickerCalendarBtnActiveColor: '#003099',

    datepickerCalendarBtnDisabledBg: '#E6E9ED',
    datepickerCalendarBtnDisabledColor: '#959EAB',

    datepickerButtonTertiaryEnable: '#828C99',
    datepickerButtonTertiaryHover: '#717985',
    datepickerButtonTertiaryActive: '#5D6570',
    datepickerButtonTertiaryDisable: '#BCC4CF',

    popoverBorderColor: 'white',

    /* Divider */
    dividerBorderColor: '#E6E9ED',

    /* Link */
    linkColor: '#003099',
    linkHoverColor: '#0043BF',
    linkActiveColor: '#0459E0',
    linkVisitedColor: '#7625B8',
    linkVisitedActiveColor: '#520787',
    linkVisitedHoverColor: '#62169C',

    /* Breadcrumbs */
    breadcrumbsColor: '#5D6570',
    breadcrumbsHoverColor: '#383D45',
    breadcrumbsActiveColor: '#24282E',

    /* Popover */
    popoverInfoColor: '#5D6570',
    popoverInfoHoverColor: '#383D45',
    popoverInfoActiveColor: '#24282E',

    /* Modal */
    modalDividerColor: '#E6E9ED',

    modalCloseButtonColor: '#13151A',
    modalCloseButtonBg: '#F5F6F7',
    modalCloseButtonHoverBg: '#E6E9ED',
    modalCloseButtonActiveBg: '#D1D8E0',

    /* InfoModal */
    'infoModal-error-InternalRingColor': 'transparent',
    'infoModal-error-ExternalRingColor': 'transparent',
    'infoModal-error-Color': '#CC0029',
    'infoModal-warning-InternalRingColor': 'transparent',
    'infoModal-warning-ExternalRingColor': 'transparent',
    'infoModal-warning-Color': '#F27F0C',
    'infoModal-success-InternalRingColor': 'transparent',
    'infoModal-success-ExternalRingColor': 'transparent',
    'infoModal-success-Color': '#00A674',

    /* Message */
    messageErrorBg: '#FEE5EA',
    messageErrorBorderColor: '#FEE5EA',
    messageErrorBorderLeftColor: '#FEE5EA',

    messageWarningBg: '#FFEACC',
    messageWarningBorderColor: '#FFEACC',
    messageWarningBorderLeftColor: '#FFEACC',

    messageSuccessBg: '#D2F7EB',
    messageSuccessBorderColor: '#D2F7EB',
    messageSuccessBorderLeftColor: '#D2F7EB',

    messageInfoBg: '#DEEDFF',
    messageInfoBorderColor: '#DEEDFF',
    messageInfoBorderLeftColor: '#DEEDFF',

    /* Pagination */
    pageColor: '#13151A',
    pageBg: 'white',
    pageHoverBg: '#DEEDFF',
    pageActiveBg: '#C4DFFF',
    pageCurrentBg: '#DEEDFF',
    pageCurrentHoverBg: '#C4DFFF',
    pageCurrentActiveBg: '#99C7FF',
    tooltipBg: '#383D45',

    /* ProgressBar */
    progressBarBg: '#E6E9ED',
    progressBarValue: '#00A674',

    /* Combobox */
    comboboxOptionBg: 'white',
    comboboxOptionHoverBg: '#DEEDFF',
    comboboxOptionActiveBg: '#C4DFFF',
    comboboxOptionSelectedBg: '#99C7FF',

    /* Loader */
    loaderColor: '#5D6570',

    /* Scroll */
    scrollColor: '#717985',

    /* Stepper */
    stepperColor: '#13151A',
    stepperDisabledColor: '#959EAB',

    stepperBg: 'white',
    stepperButtonHoverBg: '#F5F6F7',
    stepperInputHoverBg: '#F5F6F7',
    stepperActiveBg: '#E6E9ED',
    stepperDisabledBg: '#E6E9ED',

    /* FileUploader */
    fileCardBorderColor: '#BCC4CF',
    fileUploaderLoadingIndicatorColor: '#5D6570',
    fileIconColor: '#003099',
    fileUploaderMessageBtnColor: '#13151A',
    fileUploaderMessageBtnHoverColor: '#383D45',
    fileUploaderMessageBtnActiveColor: '#4B525C',

    /* Navigation */
    navListBorderBottomColor: '#E6E9ED',
    /* item-1 */
    navLinkItem1TextColor: '#13151A',
    navLinkItem1BorderBottomColor: 'transparent',
    navLinkItem1HoverBorderBottomColor: '#D1D8E0',
    navLinkItem1ActiveBorderBottomColor: '#D1D8E0',

    navLinkItem1SelectedTextColor: '#13151A',
    navLinkItem1SelectedBorderBottomColor: '#E51740',

    /* item-2 */
    navLinkItem2TextColor: '#13151A',
    navLinkItem2BorderBottomColor: 'transparent',
    navLinkItem2HoverBorderBottomColor: '#D1D8E0',
    navLinkItem2ActiveBorderBottomColor: '#D1D8E0',

    navLinkItem2SelectedTextColor: '#13151A',
    navLinkItem2SelectedBorderBottomColor: '#13151A',

    /* item-3 */
    navLinkItem3TextColor: '#13151A',
    navLinkItem3BorderBottomColor: 'transparent',
    navLinkItem3HoverBorderBottomColor: '#D1D8E0',
    navLinkItem3ActiveBorderBottomColor: '#D1D8E0',

    navLinkItem3SelectedTextColor: '#003099',
    navLinkItem3SelectedBorderBottomColor: '#003099',

    /* Tabs */
    /* Tab-1 */
    tab1Bg: '#F5F6F7',
    tab1TextColor: '#13151A',
    tab1BorderColor: '#F5F6F7',

    tab1HoverBg: '#E6E9ED',
    tab1HoverBorderColor: '#E6E9ED',
    tab1HoverTextColor: '#13151A',

    tab1ActiveBg: '#D1D8E0',
    tab1ActiveBorderColor: '#D1D8E0',
    tab1ActiveTextColor: '#13151A',

    tab1DisabledBg: '#F5F6F7',
    tab1DisabledBorderColor: '#F5F6F7',
    tab1DisabledTextColor: '#959EAB',

    /* Tab-1 selected */
    tab1SelectedTextColor: '#13151A',

    tab1SelectedFocusBg: '#FFFFFF',
    tab1SelectedFocusBorderColor: '#FFFFFF',

    tab1SelectedHoverBg: '#FFFFFF',
    tab1SelectedHoverBorderColor: '#FFFFFF',
    tab1SelectedHoverTextColor: '#383D45',

    tab1SelectedActiveBg: '#FFFFFF',
    tab1SelectedActiveBorderColor: '#FFFFFF',
    tab1SelectedActiveTextColor: '#4B525C',

    tab1SelectedDisabledBg: '#FFFFFF',
    tab1SelectedDisabledBorderColor: '#FFFFFF',
    tab1SelectedDisabledTextColor: '#959EAB',

    /* Tab-2 */
    tab2Bg: '#FFFFFF',
    tab2TextColor: '#13151A',
    tab2BorderColor: '#FFFFFF',

    tab2HoverBg: ' #F5F6F7',
    tab2HoverBorderColor: ' #F5F6F7',
    tab2HoverTextColor: '#13151A',

    tab2ActiveBg: '#E6E9ED;',
    tab2ActiveBorderColor: '#E6E9ED;',
    tab2ActiveTextColor: '#13151A',

    tab2DisabledBg: '#FFFFFF',
    tab2DisabledBorderColor: '#FFFFFF',
    tab2DisabledTextColor: '#959EAB',

    /* Tab-2 selected */
    tab2SelectedTextColor: '#13151A',

    tab2SelectedFocusBg: '#E6E9ED',
    tab2SelectedFocusBorderColor: '#E6E9ED',

    tab2SelectedHoverBg: '#D1D8E0',
    tab2SelectedHoverBorderColor: '#D1D8E0',
    tab2SelectedHoverTextColor: '#383D45',

    tab2SelectedActiveBg: '#BCC4CF',
    tab2SelectedActiveBorderColor: '#BCC4CF',
    tab2SelectedActiveTextColor: '#4B525C',

    tab2SelectedDisabledBg: '#E6E9ED',
    tab2SelectedDisabledBorderColor: '#E6E9ED',
    tab2SelectedDisabledTextColor: '#959EAB',

    /* Tab-3 */
    tab3Bg: '#DEEDFF',
    tab3TextColor: '#003099',
    tab3BorderColor: '#DEEDFF',

    tab3HoverBg: '#C4DFFF',
    tab3HoverBorderColor: '#C4DFFF',
    tab3HoverTextColor: '#003099',

    tab3ActiveBg: '#99C7FF',
    tab3ActiveBorderColor: '#99C7FF',
    tab3ActiveTextColor: '#003099',

    tab3DisabledBg: '#E6E9ED',
    tab3DisabledBorderColor: '#E6E9ED',
    tab3DisabledTextColor: '#959EAB',

    /* Tab-3 selected */
    tab3SelectedTextColor: '#FFFFFF',

    tab3SelectedFocusBg: '#003099',
    tab3SelectedFocusBorderColor: '#003099',

    tab3SelectedHoverBg: '#0043BF',
    tab3SelectedHoverBorderColor: '#0043BF',
    tab3SelectedHoverTextColor: '#FFFFFF',

    tab3SelectedActiveBg: '#0459E0',
    tab3SelectedActiveBorderColor: '#0459E0',
    tab3SelectedActiveTextColor: '#FFFFFF',

    tab3SelectedDisabledBg: '#E6E9ED',
    tab3SelectedDisabledBorderColor: '#E6E9ED',
    tab3SelectedDisabledTextColor: '#959EAB',
  } as Record<string, string>,
  fonts: {
    main: "'Golos Text', sans-serif",
    serif: "'PT Serif', serif",
    ui: "'Golos UI', sans-serif",
    headings: "'Golos Text', sans-serif",
  },
  fontSizes: {
    'fs-14': pxToRem(14),
    'fs-15': pxToRem(15),
    'fs-16': pxToRem(16),
    'fs-18': pxToRem(18),
    'fs-21': pxToRem(21),
    'fs-22': pxToRem(22),
    'fs-24': pxToRem(24),
    'fs-26': pxToRem(26),
    'fs-32': pxToRem(32),

    textBase: pxToRem(16),
    textSmall: pxToRem(14),
    textBig: pxToRem(18),

    h1: pxToRem(28),
    h1Big: pxToRem(32),

    h2: pxToRem(24),
    h2Big: pxToRem(26),

    h3: pxToRem(20),
    h3Big: pxToRem(22),

    h4: pxToRem(18),
    h4Big: pxToRem(18),

    h5: pxToRem(16),
    h5Big: pxToRem(16),

    h6: pxToRem(14),
    h6Big: pxToRem(14),

    btn32FontSize: pxToRem(14),
    btn40FontSize: pxToRem(14),
    btn48FontSize: pxToRem(16),
    btn56FontSize: pxToRem(18),

    errorFontSize: pxToRem(14),
    hintFontSize: pxToRem(14),

    paginationFontSize: pxToRem(14),

    fileUploaderMessageFontSize: pxToRem(16),

    /* Navigation */
    navLinkSize1FontSize: pxToRem(14),
    navLinkSize2FontSize: pxToRem(16),

    /* tabs */
    defaultTabSize1FontSize: pxToRem(14),
    defaultTabSize2FontSize: pxToRem(14),
    defaultTabSize3FontSize: pxToRem(16),
    defaultTabSize4FontSize: pxToRem(16),

    tab1Size1FontSize: pxToRem(14),
    tab1Size2FontSize: pxToRem(14),
    tab1Size3FontSize: pxToRem(14),
    tab1Size4FontSize: pxToRem(16),
  } as Record<string, string>,
  fontWeights: {
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,

    h1FontWeight: 600,
    h2FontWeight: 600,
    h3FontWeight: 600,
    h4FontWeight: 600,
    h5FontWeight: 600,
    h6FontWeight: 600,

    btn32FontWeight: 400,
    btn40FontWeight: 400,
    btn48FontWeight: 400,
    btn56FontWeight: 400,

    labelFontWeight: 500,

    /* Navigation */
    navLinkItem1FontWeight: 400,
    navLinkItem2FontWeight: 400,
    navLinkItem3FontWeight: 400,

    navLinkItem1SelectedFontWeight: 500,
    navLinkItem2SelectedFontWeight: 500,
    navLinkItem3SelectedFontWeight: 500,

    /* Tabs */

    tab1FontWeight: 400,
    tab2FontWeight: 400,
    tab3FontWeight: 400,

    /* Tab selected*/
    tab1SelectedFontWeight: 500,
    tab2SelectedFontWeight: 500,
    tab3SelectedFontWeight: 500,
  },
  lineHeights: {
    'lh-12': 1.2,
    'lh-15': 1.5,

    textBaseLineHeight: 1.5,
    textSmallLineHeight: 1.428,
    textBigLineHeight: 1.444,

    h1LineHeight: 1.142,
    h1BigLineHeight: 1.25,

    h2LineHeight: 1.166,
    h2BigLineHeight: 1.23,

    h3LineHeight: 1.2,
    h3BigLineHeight: 1.272,

    h4LineHeight: 1.333,
    h4BigLineHeight: 1.333,

    h5LineHeight: 1.5,
    h5BigLineHeight: 1.5,

    h6LineHeight: 1.428,
    h6BigLineHeight: 1.428,

    btn32LineHeight: 1.145,
    btn40LineHeight: 1.145,
    btn48LineHeight: 1.25,
    btn56LineHeight: 1.34,

    tabSize1LineHeight: 1.145,
    tabSize2LineHeight: 1.145,
    tabSize3LineHeight: 1.145,
    tabSize4LineHeight: 1.145,

    calendarBtnLineHeight: 1.29,

    labelLineHeight: 1.5,
    checkableLabelLineHeight: 1.5,
    errorLineHeight: 1.428,
    hintLineHeight: 1.428,

    paginationLineHeight: 1.14,

    /* Navigation */
    navLinkSize1LineHeight: 1.145,
    navLinkSize2LineHeight: 1.25,
  } as Record<string, number>,
  letterSpacings: {
    h1LetterSpacing: '-0.02em',
    h1BigLetterSpacing: '-0.025em',

    h2LetterSpacing: '-0.015em',
    h2BigLetterSpacing: '-0.02em',

    h3LetterSpacing: '-0.01em',
    h3BigLetterSpacing: '-0.015em',

    h4LetterSpacing: '-0.005em',
    h4BigLetterSpacing: '-0.01em',

    h5LetterSpacing: '0',
    h5BigLetterSpacing: '-0.005em',

    h6LetterSpacing: '0',
    h6BigLetterSpacing: '0',
  },
  borders: {
    0: `0 solid currentColor`,
    1: `${pxToRem(1)} solid currentColor`,
    2: `${pxToRem(2)} solid currentColor`,
    4: `${pxToRem(4)} solid currentColor`,
  } as Record<0 | 1 | 2 | 4 | '0' | '1' | '2' | '4', string>,
  borderWidths: {
    0: '0',
    1: pxToRem(1),
    2: pxToRem(2),
    4: pxToRem(4),

    checkboxTickBorderWidth: `0 0 0.125rem 0.125rem`,
    radioTickBorderWidth: pxToRem(4),

    messageBorderWidth: pxToRem(1),
    messageBorderLeftWidth: pxToRem(1),

    modalHeaderBorder: '0',

    /* Navigation */
    navLinkItem1BorderWidth: 0,
    navLinkItem2BorderWidth: 0,
    navLinkItem3BorderWidth: 0,
    navLinkItem1BorderBottomWidth: pxToRem(2),
    navLinkItem2BorderBottomWidth: pxToRem(2),
    navLinkItem3BorderBottomWidth: pxToRem(2),

    /* Tabs */
    tab1BorderWidth: pxToRem(1),
    tab2BorderWidth: pxToRem(1),
    tab3BorderWidth: pxToRem(1),
  } as Record<0 | 1 | 2 | 4 | '0' | '1' | '2' | '4', string>,
  radii: {
    0: '0',
    2: pxToRem(2),
    4: pxToRem(4),
    accordion: pxToRem(8),
    round: '50%',
    popover: pxToRem(8),
    button: pxToRem(8),
    starBorderRadius: pxToRem(8),
    checkboxBorderRadius: pxToRem(4),
    stepperBorderRadius: pxToRem(8),
    stepperButtonBorderRaduis: pxToRem(4),
    fieldBorderRadius: pxToRem(8),
    linkFocusBorderRadius: pxToRem(4),
    breadCrumb: pxToRem(4),
    modal: `${pxToRem(24)} ${pxToRem(24)} 0 0`,
    modalBig: pxToRem(24),
    infomodal: pxToRem(24),
    modalClose: pxToRem(8),
    messageBorderRadius: pxToRem(12),
    paginationBorderRadius: pxToRem(8),
    tooltip: pxToRem(8),

    fileCard: pxToRem(12),

    /* Tabs */
    tab1BorderRadius: pxToRem(4),
    tab2BorderRadius: pxToRem(8),
    tab3BorderRadius: pxToRem(8),
  } as Record<0 | 2 | 4 | '0' | '2' | '4' | 'round' | string, string>,
  shadows: {
    4: '0px 2px 4px rgba(0, 20, 67, 0.08)',
    12: '0px 4px 12px rgba(0, 20, 67, 0.12)',
    32: '0px 16px 32px rgba(0, 20, 67, 0.12)',
    focus: '0 0 0 0.25rem #2684FF',
    popover: '0px 0px 0px 0.25rem rgba(0, 0, 0, 0.15)',
    modal: '0px 0px 0px 0.25rem rgba(0, 0, 0, 0.15)',
    none: 'none',
  } as Record<4 | 12 | 32 | '4' | '12' | '32' | 'focus' | 'none', string>,
  sizes: {
    auto: 'auto',
    0: 0,
    '1': expressionToWidth('1/1'),
    '1/2': expressionToWidth('1/2'),
    '1/3': expressionToWidth('1/3'),
    '2/3': expressionToWidth('1/2'),
    '1/4': expressionToWidth('1/4'),
    '3/4': expressionToWidth('3/4'),
    '1/5': expressionToWidth('1/5'),
    '2/5': expressionToWidth('2/5'),
    '3/5': expressionToWidth('3/5'),
    '4/5': expressionToWidth('4/5'),
    'inner-content': pxToRem(832),
    'semi-wide': pxToRem(724),

    btn32IconSize: '1.145em',
    btn40IconSize: '1.145em',
    btn48IconSize: '1.25em',
    btn56IconSize: '1.5rem',

    tabSize1IconSize: pxToRem(16),
    tabSize2IconSize: pxToRem(16),
    tabSize3IconSize: pxToRem(16),
    tabSize4IconSize: pxToRem(16),

    /* Accordion */
    accordionMinWidth: pxToRem(24),

    /* Radiobutton & Checkbox */
    checkableSize: pxToRem(20),

    radioTickSize: pxToRem(8),

    checkboxTickWidth: '0.6rem',
    checkboxTickHeight: '0.35rem',

    checkBoxIndeterminateWidth: pxToRem(10),
    checkBoxIndeterminateHeight: pxToRem(0),

    calendarNavBtn: pxToRem(32),
    calendarNavIcon: pxToRem(20),

    datepickerBtnIconSize: pxToRem(22),
    datepickerSingleFieldWidth: pxToRem(144),
    datepickerDayMonthFieldWidth: pxToRem(56),
    datepickerYearFieldWidth: pxToRem(80),

    /* Modal */
    modalSmallMaxWidth: pxToRem(512),
    modalMaxWidth: pxToRem(736),
    modalMinHeight: 0,

    // использовано значение в пикселях, потому что если пользователь поменяет базовый размер шрифта, то высота модалки будет значительно ограничена
    modalMaxHeight: 'calc(100% - 32px)',
    modalMaxHeightBig: '100%',

    modalCloseButtonSize: pxToRem(32),
    modalBigCloseButtonSize: pxToRem(40),

    /* InfoModal */
    infoModalMaxWidth: pxToRem(512),
    infoModalIconBlockSmall: pxToRem(48),
    infoModalIconBlock: pxToRem(48),
    infoModalIconSizeSmall: pxToRem(48),
    infoModalIconSize: pxToRem(48),

    messageIcon: pxToRem(24),

    /* FileUploader */
    fileUploaderMaxWidth: pxToRem(656),
    fileUploadBtnWidth: pxToRem(187),
    fileUploaderBtnIconSize: '1.25rem',
    fileUploaderBtnIconSizeBig: '1rem',
    fileUploaderMessageBtnSize: pxToRem(40),
    fileUploaderMessageBtnIconSize: pxToRem(24),
    fileUploaderSpinnerSize: pxToRem(20),
    fileUploaderFileIconSize: pxToRem(20),
    fileUploaderErrorIconSize: pxToRem(20),

    fileUploaderIconBlockWidth: pxToRem(20),

    /* Navigation */
    navLinkSize1MinHeight: pxToRem(40),
    navLinkSize1IconSize: pxToEm(16, 14),

    navLinkSize2MinHeight: pxToRem(48),
    navLinkSize2IconSize: pxToEm(20, 16),

    /* Tabs */
    defaultTabSize1MinHeight: pxToRem(32),
    defaultTabSize2MinHeight: pxToRem(40),
    defaultTabSize3MinHeight: pxToRem(48),
    defaultTabSize4MinHeight: pxToRem(56),

    tab1Size1MinHeight: pxToRem(24),
    tab1Size2MinHeight: pxToRem(32),
    tab1Size3MinHeight: pxToRem(40),
    tab1Size4MinHeight: pxToRem(48),
  } as Record<string, string | number>,
  zIndices: {
    0: 0,
    1: 1,
    1010: 1010,
  } as Record<0 | 1 | 1010 | '0' | '1' | '1010', number>,
  breakpoints: [
    pxToEm(768),
    pxToEm(1024),
    pxToEm(1280),
    pxToEm(1366),
    pxToEm(1600),
  ],
});

export type ThemeType30 = typeof theme30;

declare module '@emotion/react' {
  export interface Theme extends ThemeType30 {}
}

export { theme30 };
